import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {Box, Typography, Chip, Button, IconButton, Tooltip, Link, Stack} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/ConfirmDialog';
import InlineToggleCell from '../../components/InlineToggleCell';
import {ROLES} from '../../config/roles';
import Protected from '../../components/Protected';

const TYPE_COLORS = {daily: 'info', weekly: 'secondary', milestone: 'warning', custom: 'default'};

const summarize = (items) => (items || []).map((item, i) => (
    <Chip key={i} size="small" variant="outlined" label={`${item.action} ${item.entity}`} sx={{mr: 0.5, mb: 0.5}}/>
));

const ChallengesPage = () => {
    const navigate = useNavigate();
    const [challenges, setChallenges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchChallenges = useCallback(() => {
        setLoading(true);
        apiService.get('/api/challenges')
            .then(data => setChallenges(data))
            .catch(err => setError(err.message || 'Failed to fetch challenges'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/challenges/${itemToDelete.id}`);
            fetchChallenges();
        } catch (err) {
            setError('Failed to delete challenge.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const columns = useMemo(() => [
        {accessorKey: 'name', header: 'Name', size: 180,
            Cell: ({cell, row}) => <Link component={RouterLink} to={`/challenges/${row.original.id}`}>{cell.getValue()}</Link>},
        {accessorKey: 'type', header: 'Type', size: 100,
            Cell: ({cell}) => <Chip label={cell.getValue()} color={TYPE_COLORS[cell.getValue()] || 'default'} size="small"/>},
        {id: 'isActive', header: 'Active', size: 100,
            Cell: ({row}) => (
                <InlineToggleCell
                    protectedBy={ChallengesPage}
                    protectAction="update"
                    value={row.original.isActive}
                    save={(next) => apiService.put(`/api/challenges/${row.original.id}/active`, {value: next})}
                    onSaved={fetchChallenges}
                />
            )},
        {id: 'criteria', header: 'Criteria', size: 220,
            Cell: ({row}) => <Stack direction="row" flexWrap="wrap">{summarize(row.original.criteria)}</Stack>},
        {id: 'award', header: 'Award', size: 220,
            Cell: ({row}) => <Stack direction="row" flexWrap="wrap">{summarize(row.original.award)}</Stack>},
        {accessorKey: 'sortOrder', header: 'Sort Order', size: 90, Cell: ({cell}) => cell.getValue() ?? '-'},
    ], [fetchChallenges]);

    const table = useMaterialReactTable({
        columns, data: challenges,
        enablePagination: false, enableBottomToolbar: false,
        state: {isLoading: loading, showAlertBanner: !!error},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {showGlobalFilter: true, sorting: [{id: 'name', desc: false}]},
        renderTopToolbarCustomActions: () => (
            <Protected component={ChallengesPage} action="create">
                <Button variant="contained" startIcon={<AddIcon/>} onClick={() => navigate('/challenges/new')}>
                    Create Challenge
                </Button>
            </Protected>
        ),
        enableRowActions: true,
        displayColumnDefOptions: {'mrt-row-actions': {header: 'Actions', size: 100}},
        positionActionsColumn: 'last',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink} to={`/challenges/${row.original.id}`}><EditIcon/></IconButton>
                </Tooltip>
                <Protected component={ChallengesPage} action="delete">
                    <Tooltip title="Delete (deactivate)">
                        <IconButton color="error" onClick={() => { setItemToDelete(row.original); setConfirmOpen(true); }}>
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Challenges</Typography>
            <MaterialReactTable table={table}/>
            <Protected component={ChallengesPage} action="delete">
                <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDeleteItem}
                    title={`Delete Challenge: ${itemToDelete?.name}?`}
                    message="This deactivates the challenge (players already holding or having claimed it are unaffected) rather than deleting it outright."/>
            </Protected>
        </Box>
    );
};

ChallengesPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
};

export default ChallengesPage;
